import * as Sentry from "@sentry/react"
import type { Realtime, TokenRequest } from "ably"
import Ably from "ably"
import { useEffect, useEffectEvent, useState } from "react"
import { isRealtimeTokenError, requestRealtimeToken } from "@/lib/realtime-token"
import { getAccessToken } from "@/lib/token-manager"

export type RealtimeConnectionStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "failed"

interface UseAblyRealtimeClientOptions {
  userId: string | null
  tokenUrl: string
}

type RealtimeAuthCallback = (
  tokenParams: unknown,
  callback: (errorInfo: string | null, tokenRequest: TokenRequest | null) => void,
) => Promise<void>

// Realtime auth failures were previously a silent console.error inside an
// infinite reconnect loop (issue #527). Surface them to Sentry, throttled to one
// event per 60s per kind -- so a persistent failure is visible without flooding.
// Terminal failures (client is closed) are captured as exceptions; transient
// ones (Ably will retry) as warnings.
const AUTH_FAILURE_CAPTURE_THROTTLE_MS = 60_000
const lastAuthFailureCaptureMs = new Map<string, number>()

/** Test-only: reset the capture throttle so tests are order-independent. */
export const __resetAuthFailureCaptureThrottle = (): void => {
  lastAuthFailureCaptureMs.clear()
}

const captureRealtimeAuthFailure = (error: unknown, terminal: boolean): void => {
  const key = terminal ? "terminal" : "transient"
  const now = Date.now()
  if (now - (lastAuthFailureCaptureMs.get(key) ?? 0) < AUTH_FAILURE_CAPTURE_THROTTLE_MS) return
  lastAuthFailureCaptureMs.set(key, now)

  if (terminal) {
    Sentry.captureException(error, {
      tags: { area: "realtime-auth", kind: "terminal" },
    })
  } else {
    Sentry.captureMessage("Realtime token request failed (transient)", {
      level: "warning",
      tags: { area: "realtime-auth", kind: "transient" },
      extra: { error: error instanceof Error ? error.message : String(error) },
    })
  }
}

export const useAblyRealtimeClient = ({
  userId,
  tokenUrl,
}: UseAblyRealtimeClientOptions): {
  client: Realtime | null
  isConnected: boolean
  connectionStatus: RealtimeConnectionStatus
} => {
  const [client, setClient] = useState<Realtime | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<RealtimeConnectionStatus>("disconnected")

  const fetchTokenRequest = useEffectEvent(
    async (): Promise<TokenRequest> =>
      requestRealtimeToken({
        tokenUrl,
        onReconnecting: () => setConnectionStatus("reconnecting"),
      }),
  )

  // Only depends on userId: the token grants identity-based channels
  // (user:<id> + ritual-group), so the channel set never changes while logged
  // in. The client is created once per user and is NOT rebuilt on navigation --
  // which is what made the old `requestedChannels`-driven teardown race
  // ("Connection closed" on subscribe).
  useEffect(() => {
    if (!userId || !getAccessToken()) {
      setClient((currentClient: Realtime | null) => {
        currentClient?.close()
        return null
      })
      setIsConnected(false)
      setConnectionStatus("disconnected")
      return
    }

    setConnectionStatus("connecting")

    let hasConnected = false
    // Bound to THIS effect run's client so a stale authCallback (after userId
    // changes and a new client is created) closes its own client, not the new one.
    let clientForCallback: Realtime | null = null
    const authCallback: RealtimeAuthCallback = async (_tokenParams, callback) => {
      try {
        const tokenRequest = await fetchTokenRequest()
        callback(null, tokenRequest)
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to authorize realtime client"
        callback(message, null)

        if (isRealtimeTokenError(error) && error.terminal) {
          // A terminal failure (expired session that can't be refreshed) recurs
          // on every reconnect. Closing this client stops the 401 reconnect storm
          // (issue #527); a fresh login (userId change) remounts the effect.
          setConnectionStatus("failed")
          captureRealtimeAuthFailure(error, true)
          try {
            clientForCallback?.close()
          } catch {
            // best-effort; the failure was already captured above
          }
        } else {
          // Transient (e.g. 503 / network): let Ably reconnect on its own.
          setConnectionStatus("reconnecting")
          // Network-level failures (status 0: fetch rejected -- offline / DNS /
          // TLS / a backgrounded mobile tab) are mostly client connectivity noise
          // that Ably already retries; they dominated RITUALITY-WEB-1V and burn
          // Sentry quota. We don't report them here. NOTE: a genuine origin-
          // unreachable outage (CORS/TLS/DNS broken by a deploy) also lands in
          // status 0, so it is intentionally NOT surfaced via this client path --
          // that class is expected to be caught by backend/uptime monitoring.
          // Transient BACKEND failures (e.g. 503) are still reported below.
          if (!isRealtimeTokenError(error) || error.status !== 0) {
            captureRealtimeAuthFailure(error, false)
          }
        }
      }
    }

    const ablyClient = new Ably.Realtime({
      authCallback,
      clientId: userId,
      echoMessages: false,
    })

    clientForCallback = ablyClient
    setClient(ablyClient)

    const handleConnecting = () => {
      setIsConnected(false)
      setConnectionStatus(hasConnected ? "reconnecting" : "connecting")
    }
    const handleConnected = () => {
      hasConnected = true
      setIsConnected(true)
      setConnectionStatus("connected")
    }
    const handleDisconnected = () => {
      setIsConnected(false)
      setConnectionStatus("disconnected")
    }
    const handleFailed = () => {
      setIsConnected(false)
      setConnectionStatus("failed")
    }
    const handleClosed = () => {
      setIsConnected(false)
      setConnectionStatus("disconnected")
    }

    ablyClient.connection.on("connecting", handleConnecting)
    ablyClient.connection.on("connected", handleConnected)
    ablyClient.connection.on("disconnected", handleDisconnected)
    ablyClient.connection.on("failed", handleFailed)
    ablyClient.connection.on("closed", handleClosed)

    return () => {
      ablyClient.connection.off("connecting", handleConnecting)
      ablyClient.connection.off("connected", handleConnected)
      ablyClient.connection.off("disconnected", handleDisconnected)
      ablyClient.connection.off("failed", handleFailed)
      ablyClient.connection.off("closed", handleClosed)
      ablyClient.close()
      setClient((currentClient: Realtime | null) =>
        currentClient === ablyClient ? null : currentClient,
      )
      setIsConnected(false)
      setConnectionStatus("disconnected")
    }
  }, [userId])

  return {
    client,
    isConnected,
    connectionStatus,
  }
}
